import axios from "axios";
import { io } from "socket.io-client";
import businessRequest from "@/utils/request";

export const OPENWEBUI_TOKEN_KEY = "openwebui_token";
export const OPENWEBUI_SELECTED_MODEL_KEY = "openwebui_selected_model";
export const GUEST_AI_ID_KEY = "xsou_guest_ai_id";
export const GUEST_AI_SESSION_KEY = "xsou_guest_ai_session_v1";

const createGuestMigrationKey = (guestId) =>
  `guest-${guestId}-${Date.now()}-${Math.random().toString(36).slice(2)}`.slice(
    0,
    160
  );

export const getGuestAiId = () => {
  let guestId = localStorage.getItem(GUEST_AI_ID_KEY) || "";
  if (!/^[A-Za-z0-9_-]{16,80}$/.test(guestId)) {
    const randomPart =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
            .toString(36)
            .slice(2)}`;
    guestId = `web-${randomPart}`.replace(/[^A-Za-z0-9_-]/g, "_");
    localStorage.setItem(GUEST_AI_ID_KEY, guestId);
  }
  return guestId;
};

const normalizeGuestSessionMessages = (messages) =>
  (Array.isArray(messages) ? messages : [])
    .filter(
      (message) =>
        message &&
        ["user", "assistant"].includes(message.role) &&
        String(message.content || "").trim()
    )
    .slice(-10)
    .map((message) => ({
      id: String(message.id || ""),
      role: message.role,
      content: String(message.content || ""),
      parentId: message.parentId || null,
      childrenIds: Array.isArray(message.childrenIds)
        ? message.childrenIds.map(String)
        : [],
      done: message.done !== false,
      timestamp: Number(message.timestamp) || Math.floor(Date.now() / 1000),
    }));

export const getGuestAiSession = () => {
  try {
    const payload = JSON.parse(sessionStorage.getItem(GUEST_AI_SESSION_KEY) || "null");
    if (!payload || payload.version !== 1) return null;
    return {
      version: 1,
      guestId: String(payload.guestId || ""),
      messages: normalizeGuestSessionMessages(payload.messages),
      pendingQuestion: String(payload.pendingQuestion || "").slice(0, 4000),
      migrationKey: String(payload.migrationKey || "").slice(0, 160),
      updatedAt: Number(payload.updatedAt) || Date.now(),
    };
  } catch (error) {
    sessionStorage.removeItem(GUEST_AI_SESSION_KEY);
    return null;
  }
};

export const setGuestAiSession = ({ messages, pendingQuestion = "" } = {}) => {
  const guestId = getGuestAiId();
  const existing = getGuestAiSession();
  const payload = {
    version: 1,
    guestId,
    messages: normalizeGuestSessionMessages(messages),
    pendingQuestion: String(pendingQuestion || "").slice(0, 4000),
    migrationKey:
      (existing && existing.guestId === guestId && existing.migrationKey) ||
      createGuestMigrationKey(guestId),
    updatedAt: Date.now(),
  };
  sessionStorage.setItem(GUEST_AI_SESSION_KEY, JSON.stringify(payload));
  return payload;
};

export const clearGuestAiSession = () => {
  sessionStorage.removeItem(GUEST_AI_SESSION_KEY);
};

export const getGuestAiStatus = async () => {
  const response = await fetch("/dev-api/api/mts-api/guest-ai/status", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guestId: getGuestAiId() }),
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch (error) {
    payload = {};
  }
  if (!response.ok) {
    const requestError = new Error(
      payload.message || payload.msg || "游客额度查询失败"
    );
    requestError.status = response.status;
    requestError.data = payload;
    throw requestError;
  }
  return {
    maxUses: Number(payload.maxUses) || 5,
    usedCount: Math.max(0, Number(payload.usedCount) || 0),
    remainingCount: Math.max(0, Number(payload.remainingCount) || 0),
  };
};

export const getOpenWebuiBase = () => {
  const base = process.env.VUE_APP_OPENWEBUI_BASE || "/coze-api";
  return base.replace(/\/$/, "");
};

export const isOpenWebuiLocalMode = () => {
  return process.env.VUE_APP_OPENWEBUI_LOCAL_MODE === "true";
};

export const getOpenwebuiToken = () => {
  return localStorage.getItem(OPENWEBUI_TOKEN_KEY) || "";
};

export const setOpenwebuiToken = (token) => {
  if (token) {
    localStorage.setItem(OPENWEBUI_TOKEN_KEY, token);
  }
};

export const removeOpenwebuiToken = () => {
  localStorage.removeItem(OPENWEBUI_TOKEN_KEY);
};

export const getOpenwebuiSelectedModel = () => {
  return localStorage.getItem(OPENWEBUI_SELECTED_MODEL_KEY) || "";
};

export const setOpenwebuiSelectedModel = (model) => {
  if (model) {
    localStorage.setItem(OPENWEBUI_SELECTED_MODEL_KEY, model);
  } else {
    localStorage.removeItem(OPENWEBUI_SELECTED_MODEL_KEY);
  }
};

const openWebuiRequest = axios.create({
  baseURL: getOpenWebuiBase(),
  timeout: 20000,
});

openWebuiRequest.interceptors.request.use((config) => {
  const token = config.openwebuiToken || getOpenwebuiToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

openWebuiRequest.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response ? error.response.status : 0;
    const data = error.response ? error.response.data : null;
    const detail =
      (data && (data.detail || data.message || data.msg || data.error)) ||
      error.message ||
      "OpenWebUI 请求失败";
    const normalizedError = new Error(
      typeof detail === "string" ? detail : JSON.stringify(detail)
    );
    normalizedError.status = status;
    normalizedError.data = data;
    throw normalizedError;
  }
);

const buildUrl = (path) => `${getOpenWebuiBase()}${path}`;

let openWebuiSocket = null;
let openWebuiSocketToken = "";
let heartbeatTimer = null;
let socketConnectHandler = null;
let socketReadyPromise = null;
let joiningSocketId = "";
let joinedSocketId = "";
let joinedSocketToken = "";

const invalidateSocketJoin = () => {
  socketReadyPromise = null;
  joiningSocketId = "";
  joinedSocketId = "";
  joinedSocketToken = "";
};

const clearSocketHeartbeat = () => {
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

export const disconnectOpenWebuiEvents = () => {
  clearSocketHeartbeat();
  if (openWebuiSocket) {
    openWebuiSocket.disconnect();
    openWebuiSocket = null;
  }
  openWebuiSocketToken = "";
  socketConnectHandler = null;
  invalidateSocketJoin();
};

const joinOpenWebuiSocket = (token, onSession) =>
  new Promise((resolve, reject) => {
    const socket = openWebuiSocket;
    socket
      .timeout(10000)
      .emit("user-join", { auth: { token } }, (error, user) => {
        if (error || !user || !user.id) {
          reject(error || new Error("OpenWebUI 事件鉴权失败"));
          return;
        }
        if (socket !== openWebuiSocket || !socket.connected) {
          reject(new Error("OpenWebUI 事件连接已失效"));
          return;
        }
        onSession(socket.id);
        clearSocketHeartbeat();
        heartbeatTimer = window.setInterval(() => {
          if (openWebuiSocket && openWebuiSocket.connected) {
            openWebuiSocket.emit("heartbeat", {});
          }
        }, 30000);
        resolve(socket.id);
      });
  });

export const connectOpenWebuiEvents = ({ token, onEvent, onSession }) => {
  if (!token) {
    return Promise.reject(new Error("OpenWebUI token 缺失"));
  }

  if (openWebuiSocket && openWebuiSocketToken !== token) {
    disconnectOpenWebuiEvents();
  }

  if (!openWebuiSocket) {
    const baseUrl = new URL(getOpenWebuiBase(), window.location.origin);
    const basePath = baseUrl.pathname.replace(/\/$/, "");
    openWebuiSocket = io(baseUrl.origin, {
      path: `${basePath}/ws/socket.io`,
      transports: ["websocket", "polling"],
      tryAllTransports: true,
      auth: { token },
      reconnection: true,
    });
    openWebuiSocketToken = token;
    openWebuiSocket.on("disconnect", () => {
      invalidateSocketJoin();
      onSession("");
    });
  }

  openWebuiSocket.off("events");
  openWebuiSocket.on("events", onEvent);
  if (socketConnectHandler) {
    openWebuiSocket.off("connect", socketConnectHandler);
  }
  socketConnectHandler = () => {
    // always_connect 只代表传输层连接成功；必须等待 user-join ack 后再提交生成任务。
    const socket = openWebuiSocket;
    invalidateSocketJoin();
    joiningSocketId = socket.id;
    socketReadyPromise = joinOpenWebuiSocket(token, onSession)
      .then((socketId) => {
        if (
          socket === openWebuiSocket &&
          socket.connected &&
          socket.id === socketId
        ) {
          joinedSocketId = socketId;
          joinedSocketToken = token;
        }
        return socketId;
      })
      .catch((error) => {
        invalidateSocketJoin();
        onSession("");
        throw error;
      });
    // 自动重连时可能没有当前调用方 await；附加处理器避免浏览器报告未处理拒绝。
    socketReadyPromise.catch(() => {});
  };
  openWebuiSocket.on("connect", socketConnectHandler);

  if (openWebuiSocket.connected) {
    if (joinedSocketId === openWebuiSocket.id && joinedSocketToken === token) {
      onSession(openWebuiSocket.id);
      return Promise.resolve(openWebuiSocket.id);
    }
    if (socketReadyPromise && joiningSocketId === openWebuiSocket.id) {
      return socketReadyPromise;
    }
    socketConnectHandler();
    return socketReadyPromise;
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("OpenWebUI 事件连接超时"));
    }, 10000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      openWebuiSocket.off("connect", handleConnect);
      openWebuiSocket.off("connect_error", handleError);
    };
    const handleConnect = async () => {
      try {
        await socketReadyPromise;
        cleanup();
        resolve(openWebuiSocket.id);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    const handleError = (error) => {
      cleanup();
      reject(error);
    };
    openWebuiSocket.once("connect", handleConnect);
    openWebuiSocket.once("connect_error", handleError);
  });
};

export const signin = (data) => {
  return openWebuiRequest.post("/api/v1/auths/signin", data);
};

let supplyDemandToken = "";
let supplyDemandSigninPromise = null;

const signinSupplyDemand = async () => {
  if (supplyDemandToken) return supplyDemandToken;
  if (!supplyDemandSigninPromise) {
    // 供需识别使用固定的先搜 AI 服务账号，不覆盖聊天模块当前用户的 token。
    supplyDemandSigninPromise = signin({
      email: "xsou-17307846384@xsou.local",
      password: "xsou-openwebui-17307846384",
    })
      .then((payload) => {
        const token =
          payload.token ||
          payload.access_token ||
          (payload.data && (payload.data.token || payload.data.access_token));
        if (!token) throw new Error("供需识别登录成功但未返回 token");
        supplyDemandToken = token;
        return token;
      })
      .finally(() => {
        supplyDemandSigninPromise = null;
      });
  }
  return supplyDemandSigninPromise;
};

export const parseSupplyDemandByAi = async (data) => {
  const requestParse = async () =>
    openWebuiRequest.post("/api/v1/supply-demand/parse", data, {
      // 模型识别后还会补查料号参数，不能沿用普通 AI 请求的 20 秒限制。
      timeout: 120000,
      openwebuiToken: await signinSupplyDemand(),
    });
  try {
    return await requestParse();
  } catch (error) {
    if (error.status !== 401) throw error;
    supplyDemandToken = "";
    return requestParse();
  }
};

export const signup = (data) => {
  return openWebuiRequest.post("/api/v1/auths/signup", data);
};

export const getCurrentAuth = () => {
  return openWebuiRequest.get("/api/v1/auths/");
};

export const getModels = () => {
  return openWebuiRequest.get("/api/models");
};

const uploadFormData = async (path, fieldName, file) => {
  const formData = new FormData();
  formData.append(fieldName, file);
  const token = getOpenwebuiToken();
  const response = await fetch(buildUrl(path), {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  if (!response.ok) {
    let message = `文件上传失败（HTTP ${response.status}）`;
    try {
      const data = await response.json();
      message = data.detail || data.message || data.msg || message;
    } catch (error) {
      // 非 JSON 错误响应保留状态码提示。
    }
    const normalizedError = new Error(
      typeof message === "string" ? message : JSON.stringify(message)
    );
    normalizedError.status = response.status;
    throw normalizedError;
  }

  return response.json();
};

export const recognizeChipImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("source", "xsou_h5");
  const response = await businessRequest.post(
    "/api/mts-api/chip-recognition/recognize",
    formData,
    { baseURL: "/chip-recognition-api", timeout: 120000 }
  );
  const recognition = response.data || response;

  return {
    file: {
      id: recognition.recognition_id || "",
      meta: { content_type: file.type || "image/*" },
    },
    recognition: response,
  };
};

export const uploadChatDocument = (file) => {
  return uploadFormData(
    "/api/v1/files/?process=true&process_in_background=false",
    "file",
    file
  );
};

export const getChatList = (page = 1) => {
  return openWebuiRequest.get("/api/v1/chats/", {
    params: { page },
  });
};

export const searchChats = (text, page = 1) => {
  return openWebuiRequest.get("/api/v1/chats/search", {
    params: { text, page },
  });
};

export const createChat = (data, token) => {
  return openWebuiRequest.post("/api/v1/chats/new", data, token ? { openwebuiToken: token } : undefined);
};

export const getChat = (id) => {
  return openWebuiRequest.get(`/api/v1/chats/${id}`);
};

export const updateChat = (id, data) => {
  return openWebuiRequest.post(`/api/v1/chats/${id}`, data);
};

export const mergeChatMessages = (id, messages) => {
  return openWebuiRequest.post(`/api/v1/chats/${id}/messages/batch`, {
    messages,
  });
};

export const deleteChat = (id) => {
  return openWebuiRequest.delete(`/api/v1/chats/${id}`);
};

export const stopTask = (id) => {
  return openWebuiRequest.post(`/api/tasks/stop/${id}`);
};

export const chatCompletionsStream = async ({
  payload,
  token,
  signal,
  onToken,
  onReplace,
  onEvent,
  onResponse,
  url,
  includeOpenwebuiAuth = true,
  validateBusinessResponse = false,
  requireContent = false,
}) => {
  const response = await fetch(url || buildUrl("/api/chat/completions"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(includeOpenwebuiAuth && (token || getOpenwebuiToken()) && {
        Authorization: `Bearer ${token || getOpenwebuiToken()}`,
      }),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (onResponse) onResponse(response);

  if (!response.ok) {
    let message = `OpenWebUI HTTP ${response.status}`;
    let data = null;
    try {
      data = await response.json();
      const readMessage = (value) => {
        if (typeof value === "string") return value.trim();
        if (!value || typeof value !== "object") return "";
        for (const key of ["detail", "message", "msg", "error", "content"]) {
          const text = readMessage(value[key]);
          if (text) return text;
        }
        return "";
      };
      message = readMessage(data) || message;
    } catch (error) {
      try {
        message = (await response.text()) || message;
      } catch (textError) {
        // keep default message
      }
    }
    const normalizedError = new Error(message);
    normalizedError.status = response.status;
    normalizedError.data = data;
    throw normalizedError;
  }

  const contentType = response.headers.get("content-type") || "";
  const parseMaybeJson = (value) => {
    if (typeof value !== "string") return value;
    const text = value.trim();
    if (!text || !/^[{\[]/.test(text)) return value;
    try {
      return JSON.parse(text);
    } catch (error) {
      return value;
    }
  };

  const extractToolResultContent = (data) => {
    const payload = parseMaybeJson(data);
    if (!payload || typeof payload !== "object") {
      return "";
    }

    if (Array.isArray(payload.sources)) {
      for (const source of payload.sources) {
        const documents = Array.isArray(source && source.document)
          ? source.document
          : [];
        for (const document of documents) {
          const content = extractToolResultContent(document);
          if (content) return content;
        }
      }
    }

    const nested =
      payload.data ||
      payload.result ||
      payload.output ||
      payload.response ||
      payload.return ||
      null;

    // 料号规则工具返回 final_answer/message，OpenWebUI 有时只推送工具结果事件。
    // 前端需要把这类结果转成 assistant 正文，否则会误判为“返回空响应”。
    return (
      payload.final_answer ||
      payload.message ||
      payload.content ||
      (nested && extractToolResultContent(nested)) ||
      ""
    );
  };

  const extractCompletionContent = (data) => {
    const toolContent = extractToolResultContent(data);
    if (toolContent) return toolContent;

    const choices = Array.isArray(data && data.choices) ? data.choices : [];
    const firstChoice = choices[0] || {};
    return (
      (firstChoice.message && firstChoice.message.content) ||
      (firstChoice.delta && firstChoice.delta.content) ||
      firstChoice.text ||
      (data && data.content) ||
      (data && data.data && data.data.content) ||
      ""
    );
  };

  if (
    !response.body ||
    !response.body.getReader ||
    (contentType && !contentType.includes("text/event-stream"))
  ) {
    const rawText = await response.text();
    let data = rawText;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch (error) {
      // 非 JSON 响应按纯文本兜底，避免代理/模型异常时页面只留下空消息。
    }
    const businessCode = Number(data && data.code);
    const responseError =
      data &&
      typeof data === "object" &&
      (data.error ||
        data.detail ||
        (validateBusinessResponse &&
          Number.isFinite(businessCode) &&
          businessCode !== 200 &&
          (data.message || data.msg || `业务请求失败（${businessCode}）`)));
    if (responseError) {
      const normalizedError = new Error(
        typeof responseError === "string"
          ? responseError
          : responseError.message ||
              responseError.content ||
              responseError.detail ||
              "上游模型请求失败，请稍后重试。"
      );
      normalizedError.status = businessCode || response.status;
      normalizedError.data = data;
      throw normalizedError;
    }
    const content =
      typeof data === "string" ? data.trim() : extractCompletionContent(data);
    // 带 chat/session/message 标识时，OpenWebUI 改为后台任务，正文由 Socket.IO events 推送。
    if (
      data &&
      typeof data === "object" &&
      data.status === true &&
      data.task_id
    ) {
      return data;
    }
    if (content) {
      if (onReplace) {
        onReplace(content);
      } else {
        onToken(content);
      }
      return data;
    }
    throw new Error("OpenWebUI 返回空响应");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let receivedContent = false;

  const appendText = (value) => {
    if (typeof value === "string" && value) {
      onToken(value);
      return true;
    }
    return false;
  };

  const replaceText = (value) => {
    if (typeof value === "string") {
      if (onReplace) {
        onReplace(value);
        return true;
      } else if (value) {
        onToken(value);
        return true;
      }
    }
    return false;
  };

  const handleChoices = (choices = []) => {
    let updated = false;
    choices.forEach((choice) => {
      const delta = choice.delta || {};
      updated =
        appendText(
          delta.content ||
            (choice.message && choice.message.content) ||
            choice.text ||
            ""
        ) || updated;
    });
    return updated;
  };

  const handleOpenWebuiEvent = (event) => {
    const type = event && event.data && event.data.type;
    const eventData = event && event.data && event.data.data;

    if (type === "chat:completion") {
      const choices = Array.isArray(eventData && eventData.choices)
        ? eventData.choices
        : [];
      const updatedByChoices = handleChoices(choices);
      const completionContent = updatedByChoices
        ? ""
        : extractCompletionContent(eventData);
      let updatedByContent = false;
      if (completionContent) {
        // OpenWebUI 原生前端在 chat:completion.content 场景下会替换当前内容。
        updatedByContent = replaceText(completionContent);
      }
      return { handled: true, updated: updatedByChoices || updatedByContent };
    }

    if (type === "chat:message:delta" || type === "message") {
      return {
        handled: true,
        updated: appendText(
          (eventData && eventData.content) ||
            extractCompletionContent(eventData)
        ),
      };
    }

    if (type === "chat:message" || type === "replace") {
      return {
        handled: true,
        updated: replaceText(extractCompletionContent(eventData)),
      };
    }

    return { handled: false, updated: false };
  };

  const handleData = (rawData) => {
    const data = rawData.trim();
    if (!data || data === "[DONE]") {
      return { done: data === "[DONE]", updated: false };
    }

    let event;
    try {
      event = JSON.parse(data);
    } catch (error) {
      onToken(data);
      return { done: false, updated: true };
    }
    const streamError = event && (event.error || event.detail);
    if (streamError) {
      throw new Error(typeof streamError === "string" ? streamError :
        streamError.message || streamError.content || streamError.detail || "上游模型请求失败，请稍后重试。");
    }
    {
      if (Array.isArray(event.sources) && onEvent) {
        for (const source of event.sources) {
          onEvent({ data: { type: "source", data: source } });
        }
      }
      const isOpenWebuiEvent = Boolean(event && event.data && event.data.type);
      if (isOpenWebuiEvent && onEvent) {
        // OpenWebUI envelope 只交给统一事件处理器，避免与 onToken/onReplace 重复写入正文。
        onEvent(event);
        return {
          done:
            event.data.type === "chat:completion" &&
            Boolean(event.data.data && event.data.data.done),
          updated: false,
        };
      }

      const openWebuiEvent = handleOpenWebuiEvent(event);
      if (openWebuiEvent.handled) {
        return { done: false, updated: openWebuiEvent.updated };
      }

      const choices = Array.isArray(event.choices) ? event.choices : [];
      const updatedByChoices = handleChoices(choices);
      const eventContent = updatedByChoices
        ? ""
        : extractCompletionContent(event);
      const updatedByContent = eventContent ? replaceText(eventContent) : false;
      return { done: false, updated: updatedByChoices || updatedByContent };
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";

      // 同批已到达正文立即交付，避免逐 token 等动画帧人为限速；Vue 会合并渲染更新。
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const result = handleData(line.slice(5));
        if (result.updated) {
          receivedContent = true;
        }
        if (result.done) {
          if (requireContent && !receivedContent) {
            throw new Error("先搜AI返回空响应，请重试");
          }
          return;
        }
      }
    }

    if (buffer.startsWith("data:")) {
      const result = handleData(buffer.slice(5));
      if (result.updated) {
        receivedContent = true;
      }
    }
    if (requireContent && !receivedContent) {
      throw new Error("先搜AI返回空响应，请重试");
    }
  } finally {
    reader.releaseLock && reader.releaseLock();
  }
};

export const generateWikiPreviewAnswer = async ({ keyword, token, signal }) => {
  let answer = "";
  await chatCompletionsStream({
    token,
    signal,
    payload: {
      model: "ai",
      messages: [{ role: "user", content: keyword }],
      features: { wiki_preview: true },
      stream: true,
    },
    onToken: (text) => { answer += text; },
    onReplace: (text) => { answer = text; },
  });
  return answer;
};
